import { useEffect, useRef, useState } from 'react';

/**
 * Paces streamed text so it arrives word by word instead of in blobs.
 *
 * The backend already streams as fast as it can; what makes a chat feel alive
 * is not latency but cadence. SSE chunks land unevenly — a sentence, then
 * nothing for 800ms, then three paragraphs at once — and rendering each chunk
 * the moment it arrives makes the answer jump. This holds a reveal cursor just
 * behind the text and walks it forward on a fixed tick, so the reader sees
 * prose being written.
 *
 * Purely presentational: the caller still holds the whole target string, and
 * nothing here delays completion. When the turn ends, the full text shows.
 *
 * Two hard rules, both about never showing text the model did not say:
 *   - only ever return a prefix of the target
 *   - when the target stops being an extension of what is on screen (the stream
 *     pipeline rewrites answers mid-flight — citation markers stripped,
 *     scaffolding salvaged), snap to the new target rather than animating from
 *     stale text
 */

const TICK_MS = 40;

/**
 * How long the cursor takes to close whatever gap it is looking at.
 *
 * Expressed as time rather than a fixed rate, so pacing reads the same however
 * the text arrives: a 300-character reply and a 4000-character one both take
 * about this long to land, instead of the short one crawling and the long one
 * blurring past. It also bounds the lag behind a fast producer — the cursor
 * settles roughly this far behind and tracks from there.
 */
const CATCH_UP_MS = 2400;
const CATCH_UP_TICKS = CATCH_UP_MS / TICK_MS;
// Enough that a nearly-caught-up cursor still visibly moves.
const MIN_CHARS_PER_TICK = 2;

/** End `n` at the next word edge so a reveal never splits a word. */
const toWordBoundary = (text, n) => {
  if (n >= text.length) return text.length;
  const nextGap = text.indexOf(' ', n);
  const nextBreak = text.indexOf('\n', n);
  const candidates = [nextGap, nextBreak].filter((i) => i !== -1);
  if (!candidates.length) return text.length;
  const edge = Math.min(...candidates);
  // Don't stall on a run of whitespace: step past it so progress is visible.
  return edge + 1;
};

/**
 * Trim a character cursor back to the last completed word.
 *
 * The cursor advances in characters so the rate is honoured exactly; rounding
 * it forward to a word edge instead would make every tick reveal a whole word,
 * which is faster than any rate below one word per tick — short answers ran
 * away from their own pacing that way.
 */
const toRenderedEnd = (text, n) => {
  if (n >= text.length) return text.length;
  if (n <= 0) return 0;
  const shown = text.slice(0, n);
  const lastGap = Math.max(shown.lastIndexOf(' '), shown.lastIndexOf('\n'));
  return lastGap === -1 ? 0 : lastGap + 1;
};

/**
 * @param {*} text        target content; non-strings pass straight through
 * @param {boolean} isStreaming  whether more text is still expected
 * @param {{enabled?: boolean}} [options]  enabled=false returns text untouched
 *        (tables, JSON and HTML must never render half-parsed)
 * @returns {*} the portion of `text` to render right now
 */
export default function useSmoothReveal(text, isStreaming, options = {}) {
  const { enabled = true } = options;
  const isString = typeof text === 'string';

  // The cursor lives in a ref, which is what rendering reads: a mid-render
  // correction (below) has to take effect on THIS render, and state set during
  // render would not. The state exists only to schedule re-renders as the
  // interval advances. Seeded at the first word rather than 0, because an empty
  // first render makes downstream "is there content?" branches pick the
  // no-content path for a frame.
  const revealedRef = useRef(null);
  if (revealedRef.current === null) {
    // Mounting mid-turn starts at the first word — or at 0 when there is no
    // text yet, since the card usually appears before the answer does. Seeding
    // "everything" there is what made a late answer arrive instantly; the
    // empty-to-first-text case is seeded below, when the text lands.
    revealedRef.current = isStreaming && typeof text === 'string'
      ? toWordBoundary(text, 1)
      : Infinity;
  }
  const revealedSoFar = () => revealedRef.current;
  // Chars per tick, planned when text arrives rather than recomputed from the
  // shrinking gap — dividing the remaining backlog every tick decays towards
  // the target instead of reaching it, so long answers crawled at the tail.
  const stepRef = useRef(MIN_CHARS_PER_TICK);
  const backlogRef = useRef(0);

  // Whether this turn ever streamed. A message restored from the session list
  // never did, and must appear at once — nobody wants their history typed back
  // to them.
  const hasStreamedRef = useRef(false);
  if (isStreaming) hasStreamedRef.current = true;

  // The end of the stream means no more text is coming — not "show the rest
  // now". The backend often delivers the written answer in the same beat that
  // it reports completion, so snapping on that edge is exactly the case where
  // the answer would arrive with no motion at all. Keep revealing until the
  // cursor catches up, then stop.
  const behindOnText = isString && revealedSoFar() < text.length;
  const active = enabled
    && isString
    && text.length > 0
    && (isStreaming || (hasStreamedRef.current && behindOnText));

  const [, setRevealed] = useState(revealedRef.current);
  const textRef = useRef(text);

  // Keep the target readable from inside the interval, and re-seat the cursor
  // when the text was replaced rather than extended. Seating it at the new
  // text's length (not Infinity) means pacing resumes for whatever arrives
  // after the rewrite.
  if (isString && textRef.current !== text) {
    const previous = textRef.current;
    // Mounting before any text exists leaves the cursor at 0, so the first
    // characters to arrive would render as nothing until a tick moved it. Seed
    // at the first word the moment there is one, which is what an empty mount
    // was always reaching for.
    if (!previous && revealedRef.current === 0 && text) {
      revealedRef.current = toWordBoundary(text, 1);
    }
    const shownSoFar = typeof previous === 'string'
      ? previous.slice(0, Math.min(revealedRef.current, previous.length))
      : '';
    if (shownSoFar && !text.startsWith(shownSoFar)) {
      revealedRef.current = text.length;
      backlogRef.current = 0;
    }
    textRef.current = text;
  }

  useEffect(() => {
    if (!active) {
      // A live turn that has no text yet is not "nothing to pace" — it is the
      // moment before the answer arrives. Surrendering the cursor here is what
      // made a late answer appear whole.
      if (!isStreaming) {
        revealedRef.current = Infinity;
        setRevealed(Infinity);
      }
      return undefined;
    }

    const id = setInterval(() => {
      const target = textRef.current;
      if (typeof target !== 'string') return;
      const at = revealedRef.current;
      if (at >= target.length) return;

      const backlog = target.length - at;
      // Re-plan only when the gap grew, i.e. more text landed.
      if (backlog > backlogRef.current) {
        stepRef.current = Math.max(MIN_CHARS_PER_TICK, Math.ceil(backlog / CATCH_UP_TICKS));
      }
      backlogRef.current = backlog;
      const next = Math.min(at + stepRef.current, target.length);
      revealedRef.current = next;
      setRevealed(next);
    }, TICK_MS);

    return () => clearInterval(id);
  }, [active, isStreaming]);

  if (!isString) return text;
  if (!active) return text;
  return text.slice(0, toRenderedEnd(text, revealedRef.current));
}
